import { useState, useCallback, useRef } from 'react';
import type { PromptDoctorResult } from './types';
import type { ReadinessInput } from '../SunoReadiness/types';
import { runPromptDoctor } from './promptDoctorService';

interface UsePromptDoctorReturn {
  result: PromptDoctorResult | null;
  isRunning: boolean;
  error: string | null;
  run: (input: ReadinessInput) => Promise<void>;
  clear: () => void;
}

export function usePromptDoctor(): UsePromptDoctorReturn {
  const [result, setResult] = useState<PromptDoctorResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(0);

  const run = useCallback(async (input: ReadinessInput) => {
    const id = ++abortRef.current;
    setIsRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await runPromptDoctor(input);
      if (abortRef.current === id) setResult(res);
    } catch (err) {
      if (abortRef.current === id) setError(err instanceof Error ? err.message : 'Diagnosis failed.');
    } finally {
      if (abortRef.current === id) setIsRunning(false);
    }
  }, []);

  const clear = useCallback(() => {
    abortRef.current++;
    setResult(null);
    setError(null);
    setIsRunning(false);
  }, []);

  return { result, isRunning, error, run, clear };
}
